import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '../components/ui/dropdown-menu';
import {
  Building2, Plus, Search, Loader2, Pencil, Trash2,
  Phone, Mail, MapPin, FileText, User, FilePlus, ChevronDown, ChevronRight,
  MoreHorizontal, Power, PowerOff, Star, Download, ExternalLink, StickyNote,
  Users, Briefcase, Share2, Clock, AlertTriangle, RefreshCw, X,
  IndianRupee, CheckCircle2, Send, Eye, PenLine, Bell
} from 'lucide-react';
import { toast } from 'sonner';

// DCS ATS — Phase 6 "Client Hub" (6 Sep 2026).
// One place to run a client from: the list (GET /api/client-hub/summary) with
// search + Active/Inactive/All chips, an Add/Edit dialog carrying every billing and
// statutory field, admin activate/deactivate, and a row that expands in place to a
// lazy-loaded detail (GET /api/client-hub/{id}/detail) with KPIs and tabs:
// Requirements | Shared | Agreements | Contacts | Notes | Timeline.
// Create still goes through main's POST /api/clients (then PUT /api/client-hub/{id}
// for the extended fields); delete still uses DELETE /api/clients/{id}.

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '',
  contact_person: '',
  designation: '',
  email: '',
  phone: '',
  address: '',
  gstin: '',
  cin: '',
  pan: '',
  state: '',
  payment_basis: 'percent',
  payment_value: '',
  payment_due_days: '',
  notes: ''
};

const BASE_CREATE_FIELDS = ['name', 'contact_person', 'email', 'phone', 'address', 'gstin', 'notes'];
const HUB_FIELDS = [
  'name', 'contact_person', 'designation', 'email', 'phone', 'address', 'gstin', 'cin', 'pan', 'state',
  'payment_basis', 'payment_value', 'payment_due_days', 'notes'
];

const AGREEMENT_STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600',
  generated: 'bg-indigo-100 text-indigo-700',
  sent: 'bg-blue-100 text-blue-700',
  opened: 'bg-emerald-100 text-emerald-800',
  active: 'bg-green-100 text-green-800',
  archived: 'bg-slate-200 text-slate-500'
};

const REQ_STATUS_STYLE = {
  open: 'bg-green-100 text-green-800',
  in_progress: 'bg-blue-100 text-blue-700',
  on_hold: 'bg-amber-100 text-amber-800',
  closed: 'bg-slate-200 text-slate-600',
  filled: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-700'
};

const SHARED_STATUS_STYLE = {
  sourced: 'bg-slate-100 text-slate-600',
  screening: 'bg-slate-100 text-slate-600',
  shortlisted: 'bg-blue-100 text-blue-700',
  lined_up: 'bg-indigo-100 text-indigo-700',
  interviewed: 'bg-amber-100 text-amber-800',
  offered: 'bg-emerald-100 text-emerald-800',
  joined: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  dropped: 'bg-red-100 text-red-700'
};

const TIMELINE_ICON = {
  'client.created': Building2,
  'client.updated': Pencil,
  'client.activated': Power,
  'client.deactivated': PowerOff,
  'contact.added': Users,
  'contact.updated': Users,
  'contact.deleted': Users,
  note: StickyNote,
  'agreement.created': FileText,
  'agreement.sent': Send,
  'agreement.opened': Eye,
  'agreement.signed': PenLine,
  'requirement.created': Briefcase,
  'submission.added': Share2,
  'submission.stage': Share2
};

const authHeaders = (json) => {
  const token = localStorage.getItem('ats_token');
  const h = { Authorization: `Bearer ${token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

// Thin wrapper over fetch that keeps main's ats_token style and surfaces `detail`.
async function hubRequest(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const detail = data && data.detail;
    const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : `Request failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatMoney = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const humanStatus = (s) => (s ? String(s).replace(/_/g, ' ') : '-');

const paymentLabel = (c) => {
  if (!c || (c.payment_value === null || c.payment_value === undefined || c.payment_value === '')) return null;
  const v = c.payment_value;
  const basis = c.payment_basis === 'fixed' ? formatMoney(v) : `${v}% of CTC`;
  const due = c.payment_due_days !== null && c.payment_due_days !== undefined && c.payment_due_days !== ''
    ? ` · ${c.payment_due_days}d` : '';
  return `${basis}${due}`;
};

const Pill = ({ className, children, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${className || 'bg-slate-100 text-slate-600'}`}>
    {children}
  </span>
);

const KpiTile = ({ label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 min-w-[96px]" data-testid={testId}>
    <div className="text-lg font-bold text-slate-800 leading-tight">{value}</div>
    <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

const EmptyTab = ({ icon: Icon, text }) => (
  <div className="py-8 text-center text-sm text-slate-500">
    {Icon && <Icon className="w-8 h-8 mx-auto mb-2 text-slate-300" />}
    {text}
  </div>
);

// ---------------------------------------------------------------------------
// Expanded detail (lazy-loaded once per expansion)
// ---------------------------------------------------------------------------
function ClientDetail({ client, isAdmin, currentUser, onChanged, onOpenAgreements, onUploadJd, onOpenRequirements, inactive }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('requirements');

  // contacts
  const [contactForm, setContactForm] = useState(null); // null = closed, {} = add, {id} = edit
  const [contactSaving, setContactSaving] = useState(false);
  const [contactBusy, setContactBusy] = useState('');

  // notes
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteBusy, setNoteBusy] = useState('');

  // agreements
  const [pdfBusy, setPdfBusy] = useState('');

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await hubRequest(`/api/client-hub/${client.id}/detail`);
      setDetail(data);
    } catch (e) {
      console.error('Error loading client detail:', e);
      setError(e.message || 'Failed to load client detail');
    }
    setLoading(false);
  }, [client.id]);

  useEffect(() => { load(false); }, [load]);

  const refresh = async () => {
    await load(true);
    if (onChanged) onChanged();
  };

  // ---- contacts ----
  const openAddContact = () => setContactForm({
    name: '', designation: '', email: '', phone: '', whatsapp: '', is_primary: false, notify_enabled: true
  });
  const openEditContact = (c) => setContactForm({
    id: c.id,
    name: c.name || '', designation: c.designation || '', email: c.email || '',
    phone: c.phone || '', whatsapp: c.whatsapp || '',
    is_primary: !!c.is_primary, notify_enabled: c.notify_enabled !== false
  });

  const saveContact = async (e) => {
    e.preventDefault();
    if (!contactForm || !contactForm.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    setContactSaving(true);
    try {
      const body = {
        name: contactForm.name.trim(),
        designation: contactForm.designation,
        email: contactForm.email,
        phone: contactForm.phone,
        whatsapp: contactForm.whatsapp,
        is_primary: !!contactForm.is_primary,
        notify_enabled: !!contactForm.notify_enabled
      };
      if (contactForm.id) {
        // merge-never-wipe: tell the server which optional fields were blanked
        const clear = ['designation', 'email', 'phone', 'whatsapp'].filter((k) => !String(contactForm[k] || '').trim());
        await hubRequest(`/api/client-hub/${client.id}/contacts/${contactForm.id}`, { method: 'PUT', body: { ...body, clear } });
        toast.success('Contact updated');
      } else {
        await hubRequest(`/api/client-hub/${client.id}/contacts`, { method: 'POST', body });
        toast.success('Contact added');
      }
      setContactForm(null);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to save contact');
    }
    setContactSaving(false);
  };

  const makePrimary = async (c) => {
    if (c.is_primary) return;
    setContactBusy(`primary-${c.id}`);
    try {
      await hubRequest(`/api/client-hub/${client.id}/contacts/${c.id}`, { method: 'PUT', body: { is_primary: true } });
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to set primary contact');
    }
    setContactBusy('');
  };

  const toggleNotify = async (c, value) => {
    setContactBusy(`notify-${c.id}`);
    try {
      await hubRequest(`/api/client-hub/${client.id}/contacts/${c.id}`, { method: 'PUT', body: { notify_enabled: !!value } });
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update contact');
    }
    setContactBusy('');
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Remove contact ${c.name}?`)) return;
    setContactBusy(`delete-${c.id}`);
    try {
      await hubRequest(`/api/client-hub/${client.id}/contacts/${c.id}`, { method: 'DELETE' });
      toast.success('Contact removed');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to remove contact');
    }
    setContactBusy('');
  };

  // ---- notes ----
  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) {
      toast.error('Write something first');
      return;
    }
    setNoteSaving(true);
    try {
      await hubRequest(`/api/client-hub/${client.id}/notes`, { method: 'POST', body: { text: noteText.trim() } });
      setNoteText('');
      toast.success('Note added');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to add note');
    }
    setNoteSaving(false);
  };

  const deleteNote = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    setNoteBusy(n.id);
    try {
      await hubRequest(`/api/client-hub/${client.id}/notes/${n.id}`, { method: 'DELETE' });
      toast.success('Note deleted');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete note');
    }
    setNoteBusy('');
  };

  // ---- agreements: fetch-blob with auth (same pattern as AgreementGeneratorPage) ----
  const openPdf = async (a, signed) => {
    setPdfBusy(`${a.id}${signed ? '-signed' : ''}`);
    try {
      const res = await fetch(`${API_URL}/api/agreements/${a.id}/pdf${signed ? '?signed=true' : ''}`, {
        headers: authHeaders(false)
      });
      if (res.status === 404) {
        toast.error('No PDF stored — open Agreements and Save again');
      } else if (!res.ok) {
        toast.error(`Could not fetch the PDF (${res.status})`);
      } else {
        const blob = await res.blob();
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const win = window.open(url, '_blank', 'noopener');
        if (!win) {
          // popup blocked — fall back to a download
          const link = document.createElement('a');
          link.href = url;
          link.download = `dcs-agreement-${String(a.id).slice(0, 8)}${signed ? '-signed' : ''}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      console.error('Error fetching agreement PDF:', err);
      toast.error('Could not fetch the PDF');
    }
    setPdfBusy('');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-sm text-slate-500" data-testid={`client-detail-loading-${client.id}`}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> Loading client…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm" data-testid={`client-detail-error-${client.id}`}>
        <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {error}</span>
        <Button size="sm" variant="outline" onClick={() => load(false)} data-testid={`client-detail-retry-${client.id}`}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  if (!detail) return null;

  const k = detail.kpis || {};
  const c = detail.client || {};
  const contacts = detail.contacts || [];
  const notes = detail.notes_log || [];
  const requirements = detail.requirements || [];
  const shared = detail.shared || [];
  const agreements = detail.agreements || [];
  const timeline = detail.timeline || [];
  const canDeleteNote = (n) => isAdmin || !n.by || (currentUser && n.by === currentUser.id);

  return (
    <div className={`space-y-4 ${inactive ? 'opacity-80' : ''}`} data-testid={`client-detail-${client.id}`}>
      {/* meta strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.address}</span>}
        {c.pan && <span>PAN <span className="font-mono text-slate-700">{c.pan}</span></span>}
        {c.state && <span>State <span className="text-slate-700">{c.state}</span></span>}
        {paymentLabel(c) && <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{paymentLabel(c)}</span>}
        {c.is_active === false && c.inactive_reason && (
          <span className="text-red-600">Inactive: {c.inactive_reason}</span>
        )}
        <button
          onClick={() => load(true)}
          className="ml-auto flex items-center gap-1 text-slate-400 hover:text-slate-700"
          title="Refresh"
          data-testid={`client-detail-refresh-${client.id}`}
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="flex flex-wrap gap-2" data-testid={`client-kpis-${client.id}`}>
        <KpiTile label="Open reqs" value={`${k.open_requirements || 0} / ${k.total_requirements || 0}`} testId={`kpi-open-reqs-${client.id}`} />
        <KpiTile label="Shared" value={k.candidates_shared || 0} testId={`kpi-shared-${client.id}`} />
        <KpiTile label="Interviews" value={k.interviews || 0} testId={`kpi-interviews-${client.id}`} />
        <KpiTile label="Joined" value={k.joined || 0} testId={`kpi-joined-${client.id}`} />
        <KpiTile label="Signed" value={k.agreements_signed || 0} testId={`kpi-signed-${client.id}`} />
        <KpiTile label="Pending agmts" value={k.agreements_pending || 0} testId={`kpi-pending-${client.id}`} />
        <KpiTile label="Fee recorded" value={formatMoney(k.fee_recorded)} testId={`kpi-fee-${client.id}`} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto max-md:[&>button]:min-h-[44px] max-md:[&>button]:flex-1">
          <TabsTrigger value="requirements" data-testid={`tab-requirements-${client.id}`}>Requirements ({requirements.length})</TabsTrigger>
          <TabsTrigger value="shared" data-testid={`tab-shared-${client.id}`}>Shared ({shared.length})</TabsTrigger>
          <TabsTrigger value="agreements" data-testid={`tab-agreements-${client.id}`}>Agreements ({agreements.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid={`tab-contacts-${client.id}`}>Contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid={`tab-notes-${client.id}`}>Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid={`tab-timeline-${client.id}`}>Timeline</TabsTrigger>
        </TabsList>

        {/* ---------------- Requirements ---------------- */}
        <TabsContent value="requirements">
          <div className="flex justify-end mb-2">
            <Button size="sm" variant="outline" onClick={onUploadJd} data-testid={`detail-upload-jd-${client.id}`}>
              <FilePlus className="w-3.5 h-3.5 mr-1" /> Upload JD
            </Button>
          </div>
          {requirements.length === 0 ? (
            <EmptyTab icon={Briefcase} text="No requirements yet for this client." />
          ) : (
            <div className="overflow-x-auto bg-white border rounded-lg max-md:hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Openings</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Candidates</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requirements.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`req-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <button
                          className="text-left font-medium text-slate-800 hover:underline"
                          onClick={onOpenRequirements}
                          data-testid={`req-open-${r.id}`}
                        >
                          {r.title || 'Untitled'}
                        </button>
                        {(r.location || r.priority) && (
                          <div className="text-xs text-slate-400">{[r.location, r.priority].filter(Boolean).join(' · ')}</div>
                        )}
                      </td>
                      <td className="px-3 py-2"><Pill className={REQ_STATUS_STYLE[r.status]}>{humanStatus(r.status)}</Pill></td>
                      <td className="px-3 py-2 text-right">{r.openings ?? '-'}</td>
                      <td className="px-3 py-2 text-right">{r.candidates_count ?? 0}</td>
                      <td className="px-3 py-2 text-slate-500">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Phones: the same `requirements` rows as the table above, stacked. */}
          {requirements.length > 0 && (
            <div className="hidden max-md:block space-y-2">
              {requirements.map((r) => (
                <div key={`m-${r.id}`} className="bg-white border rounded-lg p-3" data-testid={`req-card-${r.id}`}>
                  <button
                    className="text-left font-medium text-slate-800 break-words min-h-[44px] w-full"
                    onClick={onOpenRequirements}
                    data-testid={`req-open-m-${r.id}`}
                  >
                    {r.title || 'Untitled'}
                  </button>
                  {(r.location || r.priority) && (
                    <div className="text-xs text-slate-400 break-words">{[r.location, r.priority].filter(Boolean).join(' · ')}</div>
                  )}
                  <div className="mt-2"><Pill className={REQ_STATUS_STYLE[r.status]}>{humanStatus(r.status)}</Pill></div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Openings</dt><dd className="text-slate-700">{r.openings ?? '-'}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Candidates</dt><dd className="text-slate-700">{r.candidates_count ?? 0}</dd></div>
                    <div className="col-span-2 flex justify-between gap-2"><dt className="text-slate-400">Created</dt><dd className="text-slate-700">{formatDate(r.created_at)}</dd></div>
                  </dl>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Shared ---------------- */}
        <TabsContent value="shared">
          {shared.length === 0 ? (
            <EmptyTab icon={Share2} text="No candidates in this client's pipeline yet." />
          ) : (
            <div className="overflow-x-auto bg-white border rounded-lg max-md:hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Candidate</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Requirement</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Stage</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Last moved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shared.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`shared-row-${s.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{s.candidate_name || 'Candidate'}</td>
                      <td className="px-3 py-2 text-slate-600">{s.requirement_title || '-'}</td>
                      <td className="px-3 py-2"><Pill className={SHARED_STATUS_STYLE[s.status]}>{humanStatus(s.status)}</Pill></td>
                      <td className="px-3 py-2 text-slate-500">{formatDate(s.shared_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Phones: the same `shared` rows as the table above, stacked. */}
          {shared.length > 0 && (
            <div className="hidden max-md:block space-y-2">
              {shared.map((s) => (
                <div key={`m-${s.id}`} className="bg-white border rounded-lg p-3" data-testid={`shared-card-${s.id}`}>
                  <div className="font-medium text-slate-800 break-words">{s.candidate_name || 'Candidate'}</div>
                  <div className="text-sm text-slate-600 break-words">{s.requirement_title || '-'}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Pill className={SHARED_STATUS_STYLE[s.status]}>{humanStatus(s.status)}</Pill>
                    <span className="text-xs text-slate-500">{formatDate(s.shared_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Agreements ---------------- */}
        <TabsContent value="agreements">
          <div className="flex justify-end mb-2">
            <Button size="sm" variant="outline" onClick={onOpenAgreements} data-testid={`detail-open-agreements-${client.id}`}>
              <FileText className="w-3.5 h-3.5 mr-1" /> Open Agreements
            </Button>
          </div>
          {agreements.length === 0 ? (
            <EmptyTab icon={FileText} text="No agreements for this client. Use Agreements to generate one." />
          ) : (
            <div className="space-y-2">
              {agreements.map((a) => (
                <div key={a.id} className={`bg-white border rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 ${a.is_archived ? 'opacity-60' : ''}`} data-testid={`agreement-row-${a.id}`}>
                  <Pill className={AGREEMENT_STATUS_STYLE[a.status]} testId={`agreement-status-${a.id}`}>{a.status}</Pill>
                  <div className="min-w-0 flex-1 text-xs text-slate-500">
                    <span className="font-mono text-slate-700">{String(a.id || '').slice(0, 8)}</span>
                    {' · '}created {formatDate(a.created_at)}
                    {a.fee_value !== undefined && a.fee_value !== null && a.fee_value !== '' && (
                      <> · {a.fee_basis === 'fixed' ? `Fixed ${formatMoney(a.fee_value)}` : `${a.fee_value}% of CTC`}</>
                    )}
                    {a.sent_at && <> · sent {formatDate(a.sent_at)}{a.sent_to ? ` to ${a.sent_to}` : ''}</>}
                    {a.opened_at && <> · opened {formatDate(a.opened_at)}</>}
                    {a.signed_uploaded_at && <> · countersigned {formatDate(a.signed_uploaded_at)}</>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openPdf(a, false)} disabled={pdfBusy === a.id} data-testid={`agreement-pdf-${a.id}`}>
                      {pdfBusy === a.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} PDF
                    </Button>
                    {a.signed_uploaded_at && (
                      <Button size="sm" variant="outline" onClick={() => openPdf(a, true)} disabled={pdfBusy === `${a.id}-signed`} className="border-emerald-200 text-emerald-700" data-testid={`agreement-signed-pdf-${a.id}`}>
                        {pdfBusy === `${a.id}-signed` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Signed
                      </Button>
                    )}
                    {a.token && (
                      <a
                        href={`${API_URL}/api/agreements/view/${a.token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        data-testid={`agreement-open-${a.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Contacts ---------------- */}
        <TabsContent value="contacts">
          <div className="flex justify-end mb-2">
            <Button size="sm" variant="outline" onClick={openAddContact} disabled={!!contactForm} data-testid={`contact-add-${client.id}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
            </Button>
          </div>

          {contactForm && (
            <form onSubmit={saveContact} className="bg-slate-50 border rounded-lg p-3 mb-3 space-y-3" data-testid={`contact-form-${client.id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name" required data-testid="contact-name-input" />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })} placeholder="HR Head" data-testid="contact-designation-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="name@company.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input value={contactForm.whatsapp} onChange={(e) => setContactForm({ ...contactForm, whatsapp: e.target.value })} placeholder="+91 98765 43210" data-testid="contact-whatsapp-input" />
                </div>
                <div className="flex items-end gap-6 pb-1">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Switch checked={!!contactForm.is_primary} onCheckedChange={(v) => setContactForm({ ...contactForm, is_primary: v })} data-testid="contact-primary-switch" /> Primary
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Switch checked={!!contactForm.notify_enabled} onCheckedChange={(v) => setContactForm({ ...contactForm, notify_enabled: v })} data-testid="contact-notify-switch" /> Notify
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setContactForm(null)} data-testid="contact-cancel-btn">Cancel</Button>
                <Button type="submit" size="sm" disabled={contactSaving} data-testid="contact-save-btn">
                  {contactSaving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                  {contactForm.id ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          )}

          {contacts.length === 0 && !contactForm ? (
            <EmptyTab icon={Users} text="No contacts yet. Add the people you deal with at this client." />
          ) : (
            <div className="space-y-2">
              {contacts.map((ct) => (
                <div key={ct.id} className="bg-white border rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`contact-row-${ct.id}`}>
                  <button
                    onClick={() => makePrimary(ct)}
                    disabled={contactBusy === `primary-${ct.id}`}
                    className={`p-1 rounded ${ct.is_primary ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                    title={ct.is_primary ? 'Primary contact' : 'Make primary'}
                    data-testid={`contact-primary-${ct.id}`}
                  >
                    {contactBusy === `primary-${ct.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className={`w-4 h-4 ${ct.is_primary ? 'fill-current' : ''}`} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">
                      {ct.name}
                      {ct.designation && <span className="text-xs text-slate-500 font-normal"> · {ct.designation}</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                      {ct.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{ct.email}</span>}
                      {ct.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{ct.phone}</span>}
                      {ct.whatsapp && <span className="flex items-center gap-1">WA {ct.whatsapp}</span>}
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" title="Notifications">
                    <Bell className="w-3 h-3" />
                    <Switch
                      checked={ct.notify_enabled !== false}
                      disabled={contactBusy === `notify-${ct.id}`}
                      onCheckedChange={(v) => toggleNotify(ct, v)}
                      data-testid={`contact-notify-${ct.id}`}
                    />
                  </label>
                  <button onClick={() => openEditContact(ct)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded" title="Edit" data-testid={`contact-edit-${ct.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteContact(ct)} disabled={contactBusy === `delete-${ct.id}`} className="p-1.5 text-slate-400 hover:text-red-600 rounded" title="Remove" data-testid={`contact-delete-${ct.id}`}>
                    {contactBusy === `delete-${ct.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Notes ---------------- */}
        <TabsContent value="notes">
          <form onSubmit={addNote} className="flex gap-2 mb-3" data-testid={`note-form-${client.id}`}>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note about this client…"
              rows={2}
              className="flex-1"
              data-testid="note-text-input"
            />
            <Button type="submit" disabled={noteSaving || !noteText.trim()} data-testid="note-add-btn">
              {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </form>
          {notes.length === 0 ? (
            <EmptyTab icon={StickyNote} text="No notes yet." />
          ) : (
            <div className="space-y-2">
              {notes.map((n) => (
                <div key={n.id} className="bg-white border rounded-lg px-3 py-2 flex items-start gap-2" data-testid={`note-row-${n.id}`}>
                  <StickyNote className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{n.by_name || 'Staff'} · {formatDateTime(n.at)}</p>
                  </div>
                  {canDeleteNote(n) && (
                    <button onClick={() => deleteNote(n)} disabled={noteBusy === n.id} className="p-1 text-slate-300 hover:text-red-600 rounded" title="Delete note" data-testid={`note-delete-${n.id}`}>
                      {noteBusy === n.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------- Timeline ---------------- */}
        <TabsContent value="timeline">
          {timeline.length === 0 ? (
            <EmptyTab icon={Clock} text="Nothing on the timeline yet." />
          ) : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-3 py-1" data-testid={`timeline-${client.id}`}>
              {timeline.map((t, i) => {
                const Icon = TIMELINE_ICON[t.kind] || Clock;
                return (
                  <li key={`${t.at}-${i}`} className="ml-4">
                    <span className="absolute -left-[9px] mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-white border border-slate-300">
                      <Icon className="w-2.5 h-2.5 text-slate-500" />
                    </span>
                    <div className="text-sm text-slate-800">{t.text}</div>
                    <div className="text-xs text-slate-400">
                      {formatDateTime(t.at)}{t.by_name ? ` · ${t.by_name}` : ''} · <span className="font-mono">{t.kind}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function ClientsPage() {
  const navigate = useNavigate();
  const { user, canAccess } = useAuth();
  const isAdmin = !!(canAccess && canAccess('admin'));

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active'); // active | inactive | all
  // mobile-round1 (8 Sep 2026): on a phone the chips + count + refresh sit
  // behind one Filters button instead of wrapping into three ragged lines.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailVersion, setDetailVersion] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editFullLoaded, setEditFullLoaded] = useState(false); // only then may blanks be cleared server-side
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [toggleTarget, setToggleTarget] = useState(null); // client for activate/deactivate
  const [toggleReason, setToggleReason] = useState('');
  const [toggling, setToggling] = useState(false);
  // DCS-INACTIVE-CLIENTS 2026-09-07: what is about to disappear
  const [toggleImpact, setToggleImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const loadClients = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const rows = await hubRequest('/api/client-hub/summary');
      setClients(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error loading clients:', e);
      setLoadError(e.message || 'Failed to load clients');
      toast.error('Failed to load clients');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadClients(false); }, [loadClients]);

  const refreshList = useCallback(() => loadClients(true), [loadClients]);

  const counts = useMemo(() => {
    const active = clients.filter((c) => c.is_active !== false).length;
    return { active, inactive: clients.length - active, all: clients.length };
  }, [clients]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (filter === 'active' && c.is_active === false) return false;
      if (filter === 'inactive' && c.is_active !== false) return false;
      if (!q) return true;
      return [c.name, c.contact_person, c.email, c.phone, c.gstin, c.cin, c.pan, c.state]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [clients, search, filter]);

  // ---- add / edit ----
  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingClient(null);
    setEditFullLoaded(false);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const fillForm = (c) => setFormData({
    name: c.name || '',
    contact_person: c.contact_person || '',
    designation: c.designation || '',
    email: c.email || '',
    phone: c.phone || '',
    address: c.address || '',
    gstin: c.gstin || '',
    cin: c.cin || (c.agreement_defaults && c.agreement_defaults.cin) || '',
    pan: c.pan || '',
    state: c.state || '',
    payment_basis: c.payment_basis === 'fixed' ? 'fixed' : 'percent',
    payment_value: c.payment_value === null || c.payment_value === undefined ? '' : String(c.payment_value),
    payment_due_days: c.payment_due_days === null || c.payment_due_days === undefined ? '' : String(c.payment_due_days),
    notes: c.notes || ''
  });

  const openEditModal = async (client) => {
    setEditingClient(client);
    setEditFullLoaded(false);
    fillForm(client); // summary row has most fields; address/notes come from detail
    setModalOpen(true);
    setEditLoading(true);
    try {
      const d = await hubRequest(`/api/client-hub/${client.id}/detail`);
      if (d && d.client) {
        fillForm(d.client);
        setEditFullLoaded(true);
      }
    } catch (e) {
      toast.error('Could not load full client record — editing from summary');
    }
    setEditLoading(false);
  };

  const hubPayload = () => {
    const body = {};
    const clear = [];
    const mayClear = !!editingClient && editFullLoaded;
    HUB_FIELDS.forEach((k) => {
      const v = formData[k];
      if (k === 'payment_value' || k === 'payment_due_days') {
        if (v === '' || v === null || v === undefined) { if (mayClear) clear.push(k); return; }
        const n = Number(v);
        if (Number.isNaN(n)) return;
        body[k] = k === 'payment_due_days' ? Math.round(n) : n;
        return;
      }
      const s = typeof v === 'string' ? v.trim() : v;
      if (s === '' || s === null || s === undefined) {
        // merge-never-wipe: on edit, blanked optional text fields are explicitly cleared
        if (mayClear && k !== 'name' && k !== 'payment_basis') clear.push(k);
        return;
      }
      body[k] = s;
    });
    if (clear.length) body.clear = clear;
    return body;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (formData.payment_value !== '' && Number(formData.payment_value) < 0) {
      toast.error('Fee value must be 0 or more');
      return;
    }
    if (formData.payment_due_days !== '' && Number(formData.payment_due_days) < 0) {
      toast.error('Payment due days must be 0 or more');
      return;
    }

    setSaving(true);
    try {
      if (editingClient) {
        await hubRequest(`/api/client-hub/${editingClient.id}`, { method: 'PUT', body: hubPayload() });
        toast.success('Client updated successfully');
      } else {
        // Create through main's endpoint (unchanged), then write the extended fields via the hub.
        const base = {};
        BASE_CREATE_FIELDS.forEach((k) => { base[k] = formData[k]; });
        const created = await hubRequest('/api/clients', { method: 'POST', body: base });
        const newId = created && (created.id || (created.client && created.client.id));
        const extra = hubPayload();
        ['name', 'contact_person', 'email', 'phone', 'address', 'gstin', 'notes', 'clear'].forEach((k) => { delete extra[k]; });
        if (newId && Object.keys(extra).length) {
          try {
            await hubRequest(`/api/client-hub/${newId}`, { method: 'PUT', body: extra });
          } catch (err) {
            toast.error(`Client created, but extra details were not saved: ${err.message}`);
          }
        } else if (!newId && Object.keys(extra).length) {
          toast.error('Client created, but the server returned no id — edit it to add CIN/PAN/billing');
        }
        toast.success('Client created successfully');
      }
      setModalOpen(false);
      resetForm();
      await refreshList();
      setDetailVersion((v) => v + 1);
    } catch (err) {
      console.error('Error saving client:', err);
      toast.error(err.message || 'Failed to save client');
    }
    setSaving(false);
  };

  // ---- delete (existing endpoint) ----
  const confirmDelete = (client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/clients/${clientToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Client deleted successfully');
        setDeleteConfirmOpen(false);
        if (expandedId === clientToDelete.id) setExpandedId(null);
        setClientToDelete(null);
        refreshList();
      } else {
        let err = {};
        try { err = await res.json(); } catch (e) { err = {}; }
        toast.error(err.detail || 'Failed to delete client');
      }
    } catch (e) {
      console.error('Error deleting client:', e);
      toast.error('Error deleting client');
    }
    setDeleting(false);
  };

  // ---- activate / deactivate (admin) ----
  const openToggle = (client) => {
    setToggleTarget(client);
    setToggleReason('');
    // DCS-INACTIVE-CLIENTS 2026-09-07: never hide money silently - price it first.
    setToggleImpact(null);
    if (client && client.is_active !== false) {
      setImpactLoading(true);
      hubRequest(`/api/client-visibility/deactivation-impact/${client.id}`)
        .then((res) => setToggleImpact(res))
        .catch(() => setToggleImpact(null))
        .finally(() => setImpactLoading(false));
    }
  };

  const handleToggle = async () => {
    if (!toggleTarget) return;
    const deactivating = toggleTarget.is_active !== false;
    setToggling(true);
    try {
      if (deactivating) {
        await hubRequest(`/api/client-hub/${toggleTarget.id}/deactivate`, { method: 'POST', body: { reason: toggleReason.trim() || null } });
        toast.success(`${toggleTarget.name} deactivated`);
      } else {
        await hubRequest(`/api/client-hub/${toggleTarget.id}/activate`, { method: 'POST' });
        toast.success(`${toggleTarget.name} activated`);
      }
      setToggleTarget(null);
      await refreshList();
      setDetailVersion((v) => v + 1);
    } catch (err) {
      toast.error(err.message || 'Failed to update client status');
    }
    setToggling(false);
  };

  // ---- shortcuts ----
  const goUploadJd = (client) => {
    navigate(`/ats/requirements?client_id=${client.id}&client_name=${encodeURIComponent(client.name || '')}&open_jd_upload=true`);
  };
  const goAgreements = (client) => {
    navigate(`/ats/agreements?client=${encodeURIComponent(client.name || '')}`, {
      state: { client: client.name, client_id: client.id }
    });
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  // DCS-INACTIVE-CLIENTS 2026-09-07: the total still owed by inactive clients,
  // shown on the chip so it can never be forgotten behind the filter.
  const inactiveUnpaid = useMemo(
    () => clients.filter((c) => c.is_active === false)
      .reduce((sum, c) => sum + (Number(c.unpaid_fees) || 0), 0),
    [clients]
  );
  const inactiveUnpaidLabel = inactiveUnpaid >= 10000000
    ? `₹${(inactiveUnpaid / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`
    : inactiveUnpaid >= 100000
      ? `₹${(inactiveUnpaid / 100000).toFixed(2).replace(/\.?0+$/, '')}L`
      : `₹${Math.round(inactiveUnpaid).toLocaleString('en-IN')}`;

  const FILTERS = [
    ['active', 'Active'],
    ['inactive', inactiveUnpaid > 0 ? `Inactive · ${inactiveUnpaidLabel} unpaid` : 'Inactive'],
    ['all', 'All']
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="client-hub-page">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3">
          <div className="max-md:min-w-0">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-slate-600" />
              Client Hub
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every client, its requirements, pipeline, agreements, contacts and notes — in one place
            </p>
          </div>
          <Button onClick={openAddModal} data-testid="add-client-btn" className="max-md:w-full max-md:min-h-[44px]">
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>

        {/* Search and filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-3 max-md:gap-2">
              <div className="relative flex-1 min-w-[220px] max-w-md max-md:w-full max-md:min-w-0 max-md:max-w-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, contact, email, GST, CIN or PAN…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="client-search-input"
                />
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="hidden max-md:inline-flex items-center gap-2 w-full min-h-[44px] px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700"
                aria-expanded={filtersOpen}
                data-testid="client-filters-toggle"
              >
                Filters
                <span className="ml-auto text-xs font-normal text-slate-400">
                  {(FILTERS.find(([id]) => id === filter) || [null, filter])[1]} · {visible.length}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className={filtersOpen ? 'contents' : 'contents max-md:hidden'}>
              <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5 bg-white max-md:w-full max-md:flex-wrap" data-testid="client-filter-chips">
                {FILTERS.map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setFilter(id)}
                    className={`px-3 min-h-[34px] text-sm font-medium rounded-md max-md:min-h-[44px] max-md:flex-1 ${filter === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`client-filter-${id}`}
                  >
                    {label} <span className="opacity-70">({counts[id]})</span>
                  </button>
                ))}
              </div>
              <Badge variant="secondary" className="text-sm ml-auto" data-testid="client-count-badge">
                {visible.length} {visible.length === 1 ? 'Client' : 'Clients'}
              </Badge>
              <button onClick={() => loadClients(true)} className="p-2 text-slate-400 hover:text-slate-700 rounded max-md:inline-flex max-md:items-center max-md:justify-center max-md:min-h-[44px] max-md:min-w-[44px]" title="Refresh" data-testid="client-list-refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="client-list-loading">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : loadError ? (
          <Card>
            <CardContent className="py-10 text-center text-slate-600" data-testid="client-list-error">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-400" />
              <p className="font-medium">Could not load clients</p>
              <p className="text-sm text-slate-500 mt-1">{loadError}</p>
              <Button onClick={() => loadClients(false)} variant="outline" className="mt-4" data-testid="client-list-retry">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </CardContent>
          </Card>
        ) : visible.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500" data-testid="client-list-empty">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">No clients found</p>
              <p className="text-sm mt-1">
                {search ? 'Try a different search term' : filter === 'inactive' ? 'No inactive clients' : filter === 'active' && counts.all > 0 ? 'No active clients — switch to All' : 'Add your first client company'}
              </p>
              {!search && counts.all === 0 && (
                <Button onClick={openAddModal} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Company
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="client-list">
            {visible.map((client) => {
              const inactive = client.is_active === false;
              const expanded = expandedId === client.id;
              return (
                <Collapsible key={client.id} open={expanded}>
                  <div className={`bg-white rounded-lg border overflow-hidden ${inactive ? 'opacity-60 bg-slate-50' : ''} ${expanded ? 'border-slate-400 shadow-sm' : ''}`}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleExpand(client.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(client.id); } }}
                      className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors max-md:items-start max-md:gap-y-2"
                      data-testid={`client-row-${client.id}`}
                      aria-expanded={expanded}
                    >
                      <span className="text-slate-400">
                        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </span>

                      {/* name + chips */}
                      <div className="min-w-[200px] flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-800" data-testid={`client-name-${client.id}`}>{client.name}</span>
                          <Pill
                            className={inactive ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-800'}
                            testId={`client-status-${client.id}`}
                          >
                            {inactive ? 'Inactive' : 'Active'}
                          </Pill>
                          {/* DCS-INACTIVE-CLIENTS 2026-09-07: money behind the Inactive chip */}
                          {inactive && client.unpaid_fees > 0 && (
                            <Pill className="bg-red-100 text-red-700" testId={`client-unpaid-${client.id}`}>
                              {client.unpaid_fees_display || client.unpaid_fees} unpaid
                            </Pill>
                          )}
                          {inactive && (
                            <Pill className="bg-slate-200 text-slate-600" testId={`client-readonly-${client.id}`}>
                              hidden everywhere else · read-only
                            </Pill>
                          )}
                          {client.gstin && <Pill className="bg-slate-100 text-slate-600 font-mono">GST {client.gstin}</Pill>}
                          {client.cin && <Pill className="bg-slate-100 text-slate-600 font-mono">CIN {client.cin}</Pill>}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                          {client.contact_person && (
                            <span className="flex items-center gap-1"><User className="w-3 h-3" />{client.contact_person}{client.designation ? ` (${client.designation})` : ''}</span>
                          )}
                          {client.email && <span className="flex items-center gap-1 max-md:w-full max-md:min-w-0"><Mail className="w-3 h-3 max-md:shrink-0" /><span className="max-md:block max-md:truncate">{client.email}</span></span>}
                          {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{client.phone}</span>}
                          {paymentLabel(client) && <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{paymentLabel(client)}</span>}
                        </div>
                      </div>

                      {/* mini KPIs */}
                      <div className="flex items-center gap-4 text-center" data-testid={`client-mini-kpis-${client.id}`}>
                        <div title="Open requirements">
                          <div className="text-base font-semibold text-slate-800 leading-tight">{client.open_requirements || 0}</div>
                          <div className="text-[10px] text-slate-400 uppercase">open reqs</div>
                        </div>
                        <div title="Signed agreements">
                          <div className="text-base font-semibold text-slate-800 leading-tight">{client.agreements_signed || 0}</div>
                          <div className="text-[10px] text-slate-400 uppercase">signed</div>
                        </div>
                        <div className="hidden sm:block" title="Last activity">
                          <div className="text-xs text-slate-600 leading-tight">{formatDate(client.last_activity_at)}</div>
                          <div className="text-[10px] text-slate-400 uppercase">last activity</div>
                        </div>
                      </div>

                      {/* shortcuts */}
                      <div className="flex items-center gap-1 max-md:w-full max-md:justify-between max-md:gap-2 max-md:border-t max-md:border-slate-100 max-md:pt-2 max-md:[&_button]:inline-flex max-md:[&_button]:items-center max-md:[&_button]:justify-center max-md:[&_button]:min-h-[44px] max-md:[&_button]:min-w-[44px]" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => goUploadJd(client)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                          title="Upload JD (new requirement)"
                          data-testid={`add-requirement-${client.id}`}
                        >
                          <FilePlus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => goAgreements(client)}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 rounded"
                          title="Agreements"
                          data-testid={`agreements-client-${client.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(client)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                          title="Edit"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => openToggle(client)}
                            className={`p-1.5 rounded ${inactive ? 'text-slate-400 hover:text-green-600' : 'text-slate-400 hover:text-amber-600'}`}
                            title={inactive ? 'Activate' : 'Deactivate'}
                            data-testid={`toggle-active-${client.id}`}
                          >
                            {inactive ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          </button>
                        )}
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 text-slate-400 hover:text-slate-700 rounded" title="More" data-testid={`client-menu-${client.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => confirmDelete(client)} className="text-red-600 focus:text-red-700" data-testid={`delete-client-${client.id}`}>
                                <Trash2 className="w-4 h-4" /> Delete client
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>

                    <CollapsibleContent>
                      {expanded && (
                        <div className="border-t bg-slate-50/60 px-4 py-4">
                          <ClientDetail
                            key={`${client.id}-${detailVersion}`}
                            client={client}
                            isAdmin={isAdmin}
                            currentUser={user}
                            inactive={inactive}
                            onChanged={refreshList}
                            onOpenAgreements={() => goAgreements(client)}
                            onOpenRequirements={() => navigate(`/ats/requirements?client_id=${client.id}&client_name=${encodeURIComponent(client.name || '')}`)}
                            onUploadJd={() => goUploadJd(client)}
                          />
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={(o) => { setModalOpen(o); if (!o) resetForm(); }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {editingClient ? 'Edit Company' : 'Add Company'}
                {editLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </DialogTitle>
              <DialogDescription>
                {editingClient ? 'Update the company information. Blank fields are left unchanged on the server unless you clear them.' : 'Add a new client company to your roster'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
              <div>
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter company legal name"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Primary contact name" data-testid="client-contact-input" />
                </div>
                <div>
                  <Label htmlFor="designation">Designation</Label>
                  <Input id="designation" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="Head of HR" data-testid="client-designation-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="contact@company.com" data-testid="client-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="client-phone-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Full business address" rows={2} data-testid="client-address-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input id="gstin" value={formData.gstin} onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })} placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="client-gstin-input" />
                </div>
                <div>
                  <Label htmlFor="cin">CIN / LLPIN</Label>
                  <Input id="cin" value={formData.cin} onChange={(e) => setFormData({ ...formData, cin: e.target.value.toUpperCase() })} placeholder="U74999MH2015PTC123456" data-testid="client-cin-input" />
                </div>
                <div>
                  <Label htmlFor="pan">PAN</Label>
                  <Input id="pan" value={formData.pan} onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })} placeholder="AAAAA0000A" maxLength={10} data-testid="client-pan-input" />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="Maharashtra" data-testid="client-state-input" />
                </div>
              </div>

              <div className="bg-slate-50 border rounded-lg p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5 mb-3">
                  <IndianRupee className="w-3.5 h-3.5" /> Billing
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="payment_basis">Fee basis</Label>
                    <select
                      id="payment_basis"
                      value={formData.payment_basis}
                      onChange={(e) => setFormData({ ...formData, payment_basis: e.target.value })}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                      data-testid="client-payment-basis-select"
                    >
                      <option value="percent">Percent of annual CTC</option>
                      <option value="fixed">Fixed amount</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="payment_value">{formData.payment_basis === 'fixed' ? 'Fee (₹)' : 'Fee (% of CTC)'}</Label>
                    <Input id="payment_value" type="number" min="0" step="any" value={formData.payment_value} onChange={(e) => setFormData({ ...formData, payment_value: e.target.value })} placeholder={formData.payment_basis === 'fixed' ? '50000' : '8.33'} data-testid="client-payment-value-input" />
                  </div>
                  <div>
                    <Label htmlFor="payment_due_days">Payment due (days)</Label>
                    <Input id="payment_due_days" type="number" min="0" step="1" value={formData.payment_due_days} onChange={(e) => setFormData({ ...formData, payment_due_days: e.target.value })} placeholder="30" data-testid="client-payment-due-input" />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes about this client..." rows={3} data-testid="client-notes-input" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} data-testid="client-form-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || editLoading} data-testid="save-client-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingClient ? 'Update' : 'Add Company'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Activate / Deactivate Confirmation */}
        <Dialog open={!!toggleTarget} onOpenChange={(o) => { if (!o) setToggleTarget(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className={toggleTarget && toggleTarget.is_active !== false ? 'text-amber-700' : 'text-green-700'}>
                {toggleTarget && toggleTarget.is_active !== false ? 'Deactivate client' : 'Activate client'}
              </DialogTitle>
              <DialogDescription>
                {toggleTarget && toggleTarget.is_active !== false ? (
                  <>
                    {/* DCS-INACTIVE-CLIENTS 2026-09-07 */}
                    {impactLoading && <span data-testid="impact-loading">Checking what this hides…</span>}
                    {!impactLoading && toggleImpact && (
                      <span data-testid="deactivate-impact">{toggleImpact.message}</span>
                    )}
                    {!impactLoading && !toggleImpact && (
                      <>Mark <strong>{toggleTarget.name}</strong> as inactive. It stays in the records and can be reactivated later.</>
                    )}
                    <span className="block mt-2 text-xs text-slate-500">
                      Nothing is deleted. Everything stays readable here, under the Inactive filter,
                      and comes back exactly as it was if you reactivate.
                    </span>
                    {!impactLoading && toggleImpact && toggleImpact.unpaid_fees > 0 && (
                      <span className="block mt-2 text-xs font-semibold text-red-600" data-testid="deactivate-unpaid">
                        {toggleImpact.unpaid_fees_display} is still unpaid. It will only be visible
                        here, on the Inactive list.
                      </span>
                    )}
                  </>
                ) : (
                  <>Mark <strong>{toggleTarget && toggleTarget.name}</strong> as active again. Its requirements,
                    submissions and history reappear everywhere, exactly as they were.</>
                )}
              </DialogDescription>
            </DialogHeader>
            {toggleTarget && toggleTarget.is_active !== false && (
              <div>
                <Label htmlFor="inactive_reason">Reason (optional)</Label>
                <Textarea id="inactive_reason" value={toggleReason} onChange={(e) => setToggleReason(e.target.value)} placeholder="e.g. no mandates since March, moved to another agency" rows={2} data-testid="deactivate-reason-input" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setToggleTarget(null)} data-testid="toggle-cancel-btn">Cancel</Button>
              <Button onClick={handleToggle} disabled={toggling} variant={toggleTarget && toggleTarget.is_active !== false ? 'destructive' : 'default'} data-testid="toggle-confirm-btn">
                {toggling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {toggleTarget && toggleTarget.is_active !== false ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Client</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{clientToDelete?.name}</strong>? This action cannot be undone. Prefer <em>Deactivate</em> if you only want it out of the way.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="delete-cancel-btn">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-confirm-btn">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
